import { Component, EventEmitter, ElementRef, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { ProjectDetailsDto, ProjectDetailsService, UpdateProjectRequestDto, SpacerQaqcTitleDto } from '../services/project-details.service';
import { LookupService } from '../services/lookup.service';
import { SlurryService, SlurryBriefDto } from '../services/slurry.service';
import { ActivatedRoute, Router } from '@angular/router';
import { SpinnerComponent } from '../shared/spinner/spinner.component';
import { DataGridComponent, GridColumn, GridConfig } from '../shared/data-grid/data-grid.component';
import { CooperativeTestRequestComponent } from './cooperative-test-request/cooperative-test-request.component';
import { FinalReportDialogComponent } from './final-report-dialog/final-report-dialog.component';
import { SummaryReportComponent } from './summary-report/summary-report.component';
import { AppendNoteDialogComponent } from './append-note-dialog/append-note-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { ManageQaqcReadingsComponent, ManageQaqcReadingsDialogData } from './manage-qaqc-readings/manage-qaqc-readings.component';
import { UserSearchDialogComponent, UserSearchDialogResult } from '../shared/user-search-dialog/user-search-dialog.component';
import { AlternateCustomersDialogComponent } from '../shared/alternate-customers-dialog/alternate-customers-dialog.component';
import { AttachmentComponent } from './attachment-component/attachment-component';
import { AddNewSlurryModalComponent, AddNewSlurryResult } from './add-new-slurry-modal/add-new-slurry-modal.component';
import { AddNewSpacerModalComponent, AddNewSpacerResult } from './add-new-spacer-modal/add-new-spacer-modal.component';
import { LoggedUserService } from '../services/logged-user.service';
import { ConfirmDialogComponent, ConfirmDialogData } from '../shared/confirm-dialog/confirm-dialog.component';
import { DuplicateSpacerDialogComponent } from './duplicate-spacer-dialog/duplicate-spacer-dialog.component';
import { ProjectStatuses } from '../services/spacer.service';
import { getApiErrorMessage } from '../shared/api-error-message';

@Component({
  selector: 'app-project-details',
  imports: [CommonModule, FormsModule, MatIconModule, SpinnerComponent, DataGridComponent, AttachmentComponent],
  providers: [DatePipe],
  templateUrl: './project-details.component.html',
  styleUrls: ['./project-details.component.css']
})

export class ProjectDetailsComponent implements OnInit {
  @Input() projectIdInput: number | null = null;
  @Input() initialTabInput: 'project' | 'serviceCompany' | 'materialsQaqc' | 'attachments' | null = null;
  @Output() projectSaved = new EventEmitter<void>();
  @Output() backToDashboard = new EventEmitter<void>();
  /** Raised when a slurry should be opened in the dashboard content area (kept inside the layout). */
  @Output() openSlurryRequested = new EventEmitter<{
    slurryId: number;
    slurryUfid?: string;
    projectId: number | null;
    projectType: number;
    projectName: string;
    redesignComments?: string;
  }>();
  /** Raised when a spacer should be opened in the dashboard content area (kept inside the layout). */
  @Output() openSpacerRequested = new EventEmitter<{
    spacerId: number;
    projectId: number;
    projectName: string;
    blendTest: boolean;
    slurry: any;
    spacerQAQCTitles: SpacerQaqcTitleDto[];
  }>();
  projectId!: number;
  isLoading = true;
  isSaving = false;
  saveMessage: { text: string; isError: boolean } | null = null;
  savedSap: string = '';
  activeTab: string = 'project';
  showAddSlurryModal = false;
  projectTypeId: number = 0; // Default to Slurry:


  // UI model for the Project tab
  project: Partial<ProjectDetailsDto> & { slurries?: any[]; id?: number; ownerId?: number; } = {
    businessUnitName: '',
    wellName: '',
    wellLocation: '',
    receiveDate: '',
    name: '',
    status: '',
    projectType: '',
    receivedBy: '',
    assignedToCAI: '',
    requestorCAI: '',
    rigId: 0,
    customerProvidedWBS: '',
    sap: '',
    isPayAsYouGo: false,
    notes: '',
    mudTypeId: 0,
    jobTypeId: 0,
    jobSubTypeId: 0,
    isBlendTest: false,
    riserless: false,
    measuredDepth: null,
    totalVerticalDepth: null,
    pipeSize: '',
    openHoleSize: '',
    bhct: null,
    bhst: null,
    mudWeightPerGallon: null,
    alternateCustomers: [],
    waterDepth: null,
    slurries: [],
  };
  // Dropdowns
  projectTypes: any[] = [];
  rigOptions: any[] = [];
  serviceCompanies: any[] = [];
  mudTypes: any[] = [];
  jobTypes: any[] = [];
  jobSubTypes: any[] = [];

  //materials qaqc grid
  materialsQaqc: any[] = [];
  materialsQaqcLoaded = false;
  materialsQaqcColumns: GridColumn[] = [
    { key: 'name', label: 'Material Name', sortable: true, filterable: true },
    { key: 'type', label: 'Type', sortable: true, filterable: true, accessor: (item) => this.getLabel(this.materialTypeMap, item.type) },
    { key: 'cisProContainerId', label: 'CisPro Container', sortable: true, filterable: true },
    { key: 'specificGravity', label: 'Specific Gravity', sortable: true, type: 'number' },
    { key: 'ph', label: 'Ph', sortable: true, type: 'number' },
    { key: 'chlorides', label: 'Chlorides', sortable: true, type: 'number' },
    { key: 'comments', label: 'Comments', sortable: true },
    { key: 'currentAmount', label: 'Current Amount', sortable: true, type: 'number' },
    { key: 'recvAmount', label: 'Recv. Amount', sortable: true, type: 'number' },
  ];
  materialsQaqcConfig: GridConfig = {
    showColumnFilters: false,
    enableSorting: true,
    stickyHeader: true,
    pageSize: 25,
  };

  // Service company data for SCD tabs
  slurryServiceCompanyData: any = null;
  spacerServiceCompanyData: any = null;
  spacerQAQCTitles: any[] = [];

  readonly fluidLossTypeMap: { key: number; value: string }[] = [
    { key: 1, value: 'Static' },
    { key: 2, value: 'Stirred' }
  ];
  readonly rheologyTypeMap: { key: number; value: string }[] = [
    { key: 1, value: 'Surface' },
    { key: 2, value: 'Downhole' }
  ];
  readonly gelStrengthTypeMap: { key: number; value: string }[] = [
    { key: 1, value: 'SGSA' },
    { key: 2, value: 'MACS' }
  ];

  // ── Enum ID → label maps (mirror of C# backend enums) ──────
  readonly projectStatusMap: Record<number, string> = {
    1: 'Draft',
    2: 'Sent to Lab',
    3: 'Ongoing in Lab',
    4: 'Finished in Lab',
    5: 'Hold',
    6: 'Project Completed',
    7: 'Ongoing',
    8: 'Draft Again',
    9: 'Sent To Lab Again',
  };

  readonly projectTypeMap: Record<number, string> = {
    1: 'Slurry',
    2: 'Spacer',
    3: 'Non-Lab',
  };

  readonly materialTypeMap: Record<number, string> = {
    1: 'Dry',
    2: 'Liquid',
    3: 'Cement',
    4: 'Liquid Base',
    5: 'Pozolan',
    6: 'Liquid Additive',
    7: 'Spacer Dry Base',
    8: 'Spacer Dry Fraction Base',
    9: 'Spacer Liquid Base',
    10: 'Spacer Liquid Fraction Base',
    11: 'Spacer Dry Additive',
    12: 'Spacer Liquid Additive',
  };

  // Project type dropdown options
  projectTypeOptions: { id: number; name: string }[] = [
    { id: 1, name: 'Slurry' },
    { id: 2, name: 'Spacer' },
    { id: 3, name: 'Non-Lab' }
  ];

  statusTypeId: number = 0; // Store numeric ID for dropdown binding
  originalStatus = '';
  loadedProjectType: string = '';
  userEmail: string | null = '';

  /** Resolve a numeric enum ID to its display label, with a fallback. */
  getLabel(map: Record<number, string>, id: number | null | undefined): string {
    if (id == null) return '';
    return map[id] ?? String(id);
  }

  private readonly emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  private isInvalidEmail(value: string | null | undefined): boolean {
    const v = (value ?? '').trim();
    return !!v && !this.emailRegex.test(v);
  }

  get receivedByError(): boolean { return this.isInvalidEmail(this.project.receivedBy); }
  get assignedToError(): boolean { return this.isInvalidEmail(this.project.assignedToCAI); }
  get requestorError(): boolean { return this.isInvalidEmail(this.project.requestorCAI); }
  get hasEmailErrors(): boolean { return this.receivedByError || this.assignedToError || this.requestorError; }

  openUserSearch(field: 'receivedBy' | 'assignedToCAI' | 'requestorCAI'): void {
    const fieldLabels: Record<string, string> = {
      receivedBy: 'Received By',
      assignedToCAI: 'Assigned To',
      requestorCAI: 'Customer',
    };
    const ref = this.dialog.open(UserSearchDialogComponent, {
      data: { fieldLabel: fieldLabels[field] },
      width: '1400px',
      maxWidth: '95vw',
      disableClose: false,
    });
    ref.afterClosed().subscribe((result: UserSearchDialogResult | undefined) => {
      if (result) {
        this.project[field] = result.email;
      }
    });
  }

  openAlternateCustomersDialog(): void {
    const existing = this.normalizeAlternateCustomers(this.project.alternateCustomers as string[] | string | null | undefined);
    const ref = this.dialog.open(AlternateCustomersDialogComponent, {
      data: { customers: existing },
      width: '520px',
      disableClose: true,
    });

    ref.afterClosed().subscribe((result: string[] | undefined) => {
      if (!result) {
        return;
      }
      this.project.alternateCustomers = result;
    });
  }

  private normalizeAlternateCustomers(value: string[] | string | null | undefined): string[] {
    if (Array.isArray(value)) {
      return value.map((item) => (item ?? '').trim()).filter((item) => !!item);
    }

    if (typeof value === 'string' && value.trim()) {
      return value
        .split(',')
        .map((item) => item.trim())
        .filter((item) => !!item);
    }

    return [];
  }

  private resolveLabel(
    map: Record<number, string>,
    raw: string | number | null | undefined
  ): string {
    if (raw == null || raw === '') return '';
    const num = typeof raw === 'number' ? raw : parseInt(raw as string, 10);
    if (!isNaN(num) && map[num]) {
      return map[num];
    }
    return String(raw); // already a string label from the API
  }

  readonly spacerCompatibilityFluidTypeMap: Record<number, string> = {
    1: 'Mud',
    2: 'Cement',
    3: 'Spacer',
  };

  /**
   * Generates the fluid mixture label for a spacer compatibility reading,
   * preserving the old WPF label-generation logic.
   * e.g. type1=Mud, type2=Spacer, spacerPercent=5 => "95% Mud 5% Spacer"
   */
  getSpacerCompatibilityLabel(reading: any): string {
    // Prefer the pre-computed value returned by the API
    //if (reading.fluidMixture) return reading.fluidMixture;
    // Fallback: compute client-side for newly-added rows not yet saved
    const firstType = this.spacerCompatibilityFluidTypeMap[reading.spacerCompatibilityRatioType1] ?? '';
    const secondType = this.spacerCompatibilityFluidTypeMap[reading.spacerCompatibilityRatioType2] ?? '';
    const secondPercent = reading.spacerPercent ?? 0;
    const firstPercent = 100 - secondPercent;

    const parts: string[] = [];
    if (firstPercent > 0) parts.push(`${firstPercent}% ${firstType}`);
    if (secondPercent > 0) parts.push(`${secondPercent}% ${secondType}`);
    return parts.join(' ');
  }

  // lifecycle and initialization
  constructor(
    private route: ActivatedRoute, private router: Router,
    private projectDetailsService: ProjectDetailsService,
    private lookupService: LookupService,
    private slurryService: SlurryService,
    private loggedUserService: LoggedUserService,
    private datePipe: DatePipe,
    private dialog: MatDialog
  ) { }

  get formattedReceiveDate(): string {
    return this.datePipe.transform(this.project.receiveDate, 'MM/dd/yyyy') ?? '';
  }

  get selectedServiceCompanyName(): string {
    const serviceCompanyId = this.project.serviceCompanyId ?? 0;
    return this.serviceCompanies.find(company => company.id === serviceCompanyId)?.name ?? '';
  }

  // Simple condition checks similar to canRemoveTitle pattern
  canShowCooperativeTestsButton(): boolean {
    return this.projectId > 0 && this.loadedProjectType !== 'Non-Lab';
  }

  canShowSummaryReportButton(): boolean {
    return this.projectId > 0 && this.loadedProjectType !== 'Non-Lab';
  }

  canShowFinalReportButton(): boolean {
    return this.projectId > 0 && this.originalStatus !== 'Draft' && this.originalStatus !== 'Sent to Lab';
    //return this.projectId > 0 && this.project.status !== 'Ongoing in Lab';
    //return this.projectId > 0  && this.statusTypeId >= 3;
  }

  // canShowManageQAQCButton(): boolean {
  //   return this.projectId > 0 && this.originalStatus === 'Draft';
  // }

  canShowMaterialsQAQCTab(): boolean {
    return this.projectId > 0 && this.loadedProjectType !== 'Non-Lab';
  }

  canShowServiceCompanyTab(): boolean {
    return this.projectId > 0 && this.loadedProjectType !== 'Non-Lab';
  }

  // ProjectViewServiceCompanyData: projectId > 0 && projectType != NonLab
  canViewServiceCompanyData(): boolean {
    return this.projectId > 0 && this.loadedProjectType !== 'Non-Lab';
  }

  // ProjectEditServiceCompanyData: projectId > 0 && projectType == Slurry && projectStatus != Completed
  canEditServiceCompanyData(): boolean {
    return this.projectId > 0 && (this.loadedProjectType === 'Slurry' || this.loadedProjectType === 'Spacer') && !this.isProjectCompleted;
  }

  get isProjectCompleted(): boolean {
    return this.project.status === 'Project Completed' || this.project.status === 'Completed';
  }

  canAddNewMix(): boolean {
    return this.projectId > 0 && !this.isProjectCompleted;
  }

  canUpdateProject(): boolean {
    return this.projectId > 0 && !this.isProjectCompleted;
  }

  canCompleteProject(): boolean {
    // Mirrors WPF CanCompleteProject: project must exist and have a non-empty SAP
    // Uses savedSap (last value persisted to server) so button only enables after Update is clicked
    return this.projectId > 0
      && !this.isProjectCompleted
      && !!this.savedSap?.trim();
  }

  canEditNotes(): boolean {
    //projectStatus == Draft || projectStatus == DraftAgain || projectStatus == Ongoing
    return this.project.status === 'Draft' || this.project.status === 'Draft Again' || this.project.status === 'Ongoing';
  }

  canEditProjectType(): boolean {
    //Can only change project type when there are NO mixes/slurries
    return !this.project.slurries || this.project.slurries.length === 0;
  }

  onProjectTypeChange(): void {

    this.project.projectType = this.projectTypeMap[this.projectTypeId];

    // User can change dropdown, but buttons stay based on saved project type until Update Project succeeds

    // Rule 1: When changing TO Non-Lab, set status to Ongoing
    if (this.project.projectType === 'Non-Lab') {
      this.project.status = 'Ongoing';
    }
    // Rule 2: When changing FROM Non-Lab (status is Ongoing) to another type, set to Draft
    else if (this.project.status === 'Ongoing') {
      this.project.status = 'Draft';
    }
    // Rule 3: Otherwise, preserve the current status (e.g., Completed, Hold, etc.)

    this.statusTypeId =
      Number(
        Object.keys(this.projectStatusMap)
          .find(key => this.projectStatusMap[+key] === this.project.status)
      ) || 0;

  }


  ngOnInit(): void {

    this.userEmail = this.loggedUserService.getCurrentEmail();
    this.applyInitialTab(this.initialTabInput);
    // Load dropdowns once
    this.lookupService.getRigs().subscribe(rigs => this.rigOptions = rigs);
    this.lookupService.getServiceCompanies().subscribe(companies => this.serviceCompanies = companies);
    this.lookupService.getMudTypes().subscribe(muds => this.mudTypes = muds);
    this.lookupService.getJobTypes().subscribe(types => this.jobTypes = types);
    this.lookupService.getJobSubTypes().subscribe(subtypes => this.jobSubTypes = subtypes);
    // If embedded in dashboard, prefer input-based loading and keep URL unchanged.
    if (Number.isFinite(this.projectIdInput) && (this.projectIdInput ?? 0) > 0) {
      this.loadProject(this.projectIdInput!);
      return;
    }
    // React to route changes so selecting another project updates this view.
    this.route.paramMap.subscribe(params => {
      const projectId = Number(params.get('projectId'));
      if (!Number.isFinite(projectId) || projectId <= 0) {
        return;
      }
      this.loadProject(projectId);
    });

  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialTabInput']) {
      this.applyInitialTab(changes['initialTabInput'].currentValue);
    }
    if (!changes['projectIdInput']) {
      return;
    }
    const projectId = Number(changes['projectIdInput'].currentValue);
    if (!Number.isFinite(projectId) || projectId <= 0 || projectId === this.projectId) {
      return;
    }
    this.loadProject(projectId);
  }

  private loadProject(projectId: number): void {
    this.isLoading = true;
    this.projectId = projectId;
    this.materialsQaqcLoaded = false;
    this.projectDetailsService.getProjectById(this.projectId).subscribe((data: ProjectDetailsDto) => {
      this.project.id = data.id;
      this.project.ownerId = (data as any).ownerId;

      // Header
      this.project.businessUnitName = data.businessUnitName || '';
      this.project.wellName = data.wellName || '';
      this.project.wellLocation = data.wellLocation || '';
      this.project.receiveDate = data.receiveDate || '';
      this.project.name = data.name || '';
      //this.project.status = this.resolveLabel(this.projectStatusMap, data.status);
      this.statusTypeId = typeof data.status === 'number' ? data.status : 0;

      this.project.status = this.projectStatusMap[this.statusTypeId];

      this.originalStatus = this.project.status
      // Project Info
      //this.project.projectType = this.resolveLabel(this.projectTypeMap, data.projectType);
      this.projectTypeId = typeof data.projectType === 'number' ? data.projectType : 1;
      this.project.projectType = this.projectTypeMap[this.projectTypeId];
      this.loadedProjectType = this.project.projectType;
      this.project.receivedBy = data.receivedBy || '';
      this.project.assignedToCAI = data.assignedToCAI || '';
      this.project.requestorCAI = data.requestorCAI || '';
      this.project.alternateCustomers = (data.alternateCustomers || []).join(', ') as any;
      this.project.expectedDueDate = data.expectedDueDate || '';
      this.project.rigName = data.rigName || '';
      this.project.customerProvidedWBS = data.customerProvidedWBS || '';
      this.project.sap = data.sap || '';
      this.savedSap = data.sap || '';
      this.project.isPayAsYouGo = !!data.isPayAsYouGo;
      this.project.notes = data.notes || '';
      this.project.serviceCompanyId = data.serviceCompanyId ?? null;
      this.project.rigId = data.rigId ?? null;
      this.project.mudTypeId = data.mudTypeId ?? null;
      this.project.jobTypeId = data.jobTypeId ?? null;
      this.project.jobSubTypeId = data.jobSubTypeId ?? null;
      this.project.isBlendTest = !!data.isBlendTest;
      this.project.riserless = !!data.riserless;
      // Measurements
      this.project.measuredDepth = data.measuredDepth ?? null;
      this.project.totalVerticalDepth = data.totalVerticalDepth ?? null;
      this.project.pipeSize = data.pipeSize || '';
      this.project.openHoleSize = data.openHoleSize || '';
      this.project.bhct = data.bhct ?? null;
      this.project.bhst = data.bhst ?? null;
      this.project.mudWeightPerGallon = data.mudWeightPerGallon ?? null;
      this.project.waterDepth = data.waterDepth ?? null;
      // Service Company Data
      this.slurryServiceCompanyData = data.slurryServiceCompanyData || null;
      this.formatSlurryTimeFields();
      this.spacerServiceCompanyData = data.spacerServiceCompanyData || null;
      this.spacerQAQCTitles = data.spacerQAQCTitles || [];
      this.project.serviceCompanyRptNumbers = data.serviceCompanyRptNumbers || '';
      // Initialize empty arrays for spacer if null
      if (this.spacerServiceCompanyData) {
        this.spacerServiceCompanyData.spacerSSSTs = this.spacerServiceCompanyData.spacerSSSTs || [];
        this.spacerServiceCompanyData.spacerQAQCs = this.spacerServiceCompanyData.spacerQAQCs || [];
        this.spacerServiceCompanyData.spacerCompatibilities = this.spacerServiceCompanyData.spacerCompatibilities || [];
        // Recover stale compatibility records that were saved without readings
        for (const compat of this.spacerServiceCompanyData.spacerCompatibilities) {
          if (!compat.readings || compat.readings.length === 0) {
            compat.readings = this.buildDefaultCompatibilityReadings();
          }
        }
      }
      // Materials QAQC
      this.materialsQaqc = data.qaqcMaterials || [];
      this.materialsQaqcLoaded = true;
      this.slurryService.getBriefByProjectId(this.projectId).subscribe((slurries: SlurryBriefDto[]) => {
        this.project.slurries = slurries;
        this.isLoading = false;
      }, () => {
        this.project.slurries = [];
        this.isLoading = false;
      });
    });
  }
  addNewSlurry(): void {
    const dialogRef = this.dialog.open(AddNewSlurryModalComponent, {
      width: '650px',
      maxHeight: '90vh',
      disableClose: true,
      data: { projectTypeId: this.getProjectTypeId() }
    });

    dialogRef.afterClosed().subscribe((result: AddNewSlurryResult | undefined) => {
      if (!result) return;

      this.openSlurryRequested.emit({
        slurryId: 0,
        projectId: this.projectId,
        projectType: this.projectTypeId,
        projectName: this.project.name ?? '',
        redesignComments: result?.comments
      });
    });
  }

   addNewSpacer(): void {
    const dialogRef = this.dialog.open(AddNewSpacerModalComponent, {
      width: '650px',
      maxHeight: '90vh',
      disableClose: true,
      data: { 
        projectTypeId: this.getProjectTypeId(), 
        projectId: this.projectId,
        projectName: this.project?.name ?? '',
        blendTest: !!this.project.isBlendTest,
        slurries: this.project.slurries ?? [],
        userEmail: this.userEmail ?? '',
        serviceCompanyName: this.selectedServiceCompanyName
        
      }
    });

    dialogRef.afterClosed().subscribe((result: AddNewSpacerResult | undefined) => {
      if (!result) return;
      this.router.navigate(['/projects-dashboard/spacer', this.projectId, 0], {
        state: {
          projectId: this.projectId,
          projectType: this.projectTypeId,
          blendTest: !!this.project.isBlendTest,
          projectName: this.project.name ?? '',
          redesign: result,
          slurries: this.project.slurries ?? [],
          userEmail: this.userEmail ?? '',
          serviceCompanyName: this.selectedServiceCompanyName
          
        }
        
      });
      
    });
  }
  /**
   * Opens an existing slurry from the Slurries table through the dashboard child route,
   * keeping the surrounding layout while updating the URL.
   */
  openSlurry(s: SlurryBriefDto): void {
    if (!s || !s.slurryID) {
      return;
    }
    this.openSlurryRequested.emit({
      slurryId: s.slurryID,
      slurryUfid: s.ufSlurryID,
      projectId: this.projectId,
      projectType: this.projectTypeId,
      projectName: this.project.name ?? ''
    });
  }

  getProjectTypeId(): number {

    // Map project type string to enum value
    // Default to Slurry (1) if not specified
    const rawProjectType = this.project.projectType;

    if (typeof rawProjectType === 'number') {
      if (rawProjectType === 2) return 2;
      if (rawProjectType === 3) return 3;
      return 1;
    }

    const projectType = String(rawProjectType ?? '').toLowerCase();
    if (projectType.includes('spacer')) return 2;
    if (projectType.includes('non') || projectType.includes('lab')) return 3;
    return 1; // Default: Slurry
  }

  private loadSlurriesForProject(): void {
    this.slurryService.getBriefByProjectId(this.projectId).subscribe({
      next: (slurries: SlurryBriefDto[]) => {
        this.project.slurries = slurries;
      },
      error: () => {
        this.project.slurries = [];
      }
    });
  }

  /**
   * IntStringToTimeConverter.Convert - Converts total minutes (as string) to HH:MM format
   * This is used when displaying the value to the user
   */
  intStringToTimeConvert(value: string | number | null | undefined): string | number {
    if (value == null || value === '') {
      return value as any;
    }

    const stringInt = String(value).trim();
    if (stringInt === '') {
      return value as any;
    }

    const totalMinutes = parseInt(stringInt, 10);
    if (isNaN(totalMinutes)) {
      return value as any;
    }

    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes - hours * 60;

    if (hours < 0 || minutes < 0) {
      return `-${Math.abs(hours)}:${Math.abs(minutes).toString().padStart(2, '0')}`;
    }

    return `${hours}:${minutes.toString().padStart(2, '0')}`;
  }

  /**
   * IntStringToTimeConverter.ConvertBack - Converts HH:MM or hours to total minutes as string
   * Exact Angular port of C# WPF converter
   */
  intStringToTimeConvertBack(value: string | number | null | undefined): string | number | null {
    if (value == null || value === '') {
      return null;
    }

    const stringInt = String(value).trim();
    if (stringInt === '') {
      return null;
    }

    const delimiterIndex = stringInt.indexOf(':');
    let hours = 0;
    let minutes = 0;

    if (delimiterIndex > 0) {
      // HH:MM format
      const hoursString = stringInt.substring(0, delimiterIndex);
      const hoursParsed = parseInt(hoursString, 10);
      if (isNaN(hoursParsed)) {
        return value as any;
      }
      hours = hoursParsed;

      if (delimiterIndex < stringInt.length - 1) {
        const minutesString = stringInt.substring(delimiterIndex + 1);
        const minutesParsed = parseInt(minutesString, 10);
        if (isNaN(minutesParsed)) {
          return value as any;
        }
        minutes = minutesParsed;
      } else {
        return value as any;
      }
    } else {
      // Plain number - treat as hours
      const hoursParsed = parseInt(stringInt, 10);
      if (isNaN(hoursParsed)) {
        return value as any;
      }
      hours = hoursParsed;
    }

    return (hours * 60 + minutes).toString();
  }

  // Legacy methods for backward compatibility
  minutesToHHMM(value: string | number | null | undefined): string {
    return this.minutesToTimeString(value);
  }

  hhmmToMinutes(value: string | number | null | undefined): number | null {
    return this.timeStringToMinutes(value);
  }

  /**
   * Converts total minutes to "HH:MM" display string (IntStringToTimeConverter.Convert)
   */
  minutesToTimeString(value: string | number | null | undefined): string {
    if (value == null || value === '') return '';
    const totalMinutes = parseInt(String(value).trim(), 10);
    if (isNaN(totalMinutes)) return '';

    const hours = Math.trunc(totalMinutes / 60);
    const minutes = totalMinutes - hours * 60;

    if (hours < 0 || minutes < 0) {
      return `-${Math.abs(hours)}:${Math.abs(minutes).toString().padStart(2, '0')}`;
    }
    return `${hours}:${minutes.toString().padStart(2, '0')}`;
  }

  /**
   * Converts "HH:MM" or plain hours to total minutes (IntStringToTimeConverter.ConvertBack)
   */
  timeStringToMinutes(value: string | number | null | undefined): number | null {
    if (value == null || String(value).trim() === '') return null;
    const stringInt = String(value).trim();

    const delimiterIndex = stringInt.indexOf(':');
    let hours = 0;
    let minutes = 0;

    if (delimiterIndex > 0) {
      const hoursParsed = parseInt(stringInt.substring(0, delimiterIndex), 10);
      if (isNaN(hoursParsed)) return null;
      hours = hoursParsed;

      if (delimiterIndex < stringInt.length - 1) {
        const minutesParsed = parseInt(stringInt.substring(delimiterIndex + 1), 10);
        if (isNaN(minutesParsed)) return null;
        minutes = minutesParsed;
      } else {
        return null;
      }
    } else {
      const hoursParsed = parseInt(stringInt, 10);
      if (isNaN(hoursParsed)) return null;
      hours = hoursParsed;
    }

    return hours * 60 + minutes;
  }

  /**
   * Normalizes a time input string on blur (e.g. "678" -> "678:00", "123:78" -> "124:18")
   */
  onTimeStringBlur(item: any, field: string): void {
    const minutes = this.timeStringToMinutes(item[field]);
    item[field] = minutes == null ? '' : this.minutesToTimeString(minutes);
  }

  /**
   * Formats a decimal input string on blur to a fixed precision (e.g. "4" -> "4.00")
   */
  onDecimalStringBlur(item: any, field: string, precision: number = 2): void {
    const value = item[field];
    if (value == null || String(value).trim() === '') {
      item[field] = '';
      return;
    }
    const parsed = parseFloat(String(value).trim());
    item[field] = isNaN(parsed) ? '' : parsed.toFixed(precision);
  }

  /**
   * Converts stored minute values in service company data to "HH:MM" display strings (on load)
   */
  private formatSlurryTimeFields(): void {
    const scd = this.slurryServiceCompanyData;
    if (!scd) return;
    (scd.thickeningTimes || []).forEach((t: any) => {
      t.time26BC = this.minutesToTimeString(t.time26BC);
      t.time70BC = this.minutesToTimeString(t.time70BC);
      t.terminationTime = this.minutesToTimeString(t.terminationTime);
    });
    (scd.compStrengthUcas || []).forEach((t: any) => {
      t.psiIn100 = this.minutesToTimeString(t.psiIn100);
      t.psiIn500 = this.minutesToTimeString(t.psiIn500);
    });
    (scd.compStrengthConditionedUCAs || []).forEach((t: any) => {
      t.psiIn100 = this.minutesToTimeString(t.psiIn100);
      t.psiIn500 = this.minutesToTimeString(t.psiIn500);
    });
  }

  /**
   * Builds a deep clone of the slurry service company data with "HH:MM" strings
   * converted back to total minutes (on save)
   */
  private buildSlurryScdForSave(): any {
    if (!this.slurryServiceCompanyData) return null;
    const clone = JSON.parse(JSON.stringify(this.slurryServiceCompanyData));
    (clone.thickeningTimes || []).forEach((t: any) => {
      t.time26BC = this.timeStringToMinutes(t.time26BC);
      t.time70BC = this.timeStringToMinutes(t.time70BC);
      t.terminationTime = this.timeStringToMinutes(t.terminationTime);
    });
    (clone.compStrengthUcas || []).forEach((t: any) => {
      t.psiIn100 = this.timeStringToMinutes(t.psiIn100);
      t.psiIn500 = this.timeStringToMinutes(t.psiIn500);
    });
    (clone.compStrengthConditionedUCAs || []).forEach((t: any) => {
      t.psiIn100 = this.timeStringToMinutes(t.psiIn100);
      t.psiIn500 = this.timeStringToMinutes(t.psiIn500);
    });
    return clone;
  }

  /**
   * DecimalStringPrecisionConverter.Convert - Formats decimal to 2 decimal places
   */
  formatDecimalPrecision(value: string | number | null | undefined, precision: number = 2): string {
    if (value == null) return '';

    const stringDecimal = String(value).trim();
    if (!stringDecimal) return '';

    const decimalValue = parseFloat(stringDecimal);
    if (isNaN(decimalValue)) return String(value);

    return decimalValue.toFixed(precision);
  }

  /**
   * DecimalStringPrecisionConverter.ConvertBack - Returns value as-is
   */
  parseDecimalValue(value: string | number | null | undefined): number | null {
    if (value == null) return null;

    const parsed = parseFloat(String(value));
    return isNaN(parsed) ? null : parsed;
  }

  /**
   * Helper to format time inputs on blur (when user leaves the field)
   */
  onTimeInputBlur(event: any, item: any, field: string): void {
    const input = event.target as HTMLInputElement;
    const value = input.value.trim();

    if (!value) {
      item[field] = null;
      input.value = '';
      return;
    }

    const minutes = this.hhmmToMinutes(value);
    if (minutes !== null) {
      item[field] = minutes;
      input.value = this.minutesToHHMM(minutes);
    } else {
      // Invalid input - reset to stored value or empty
      input.value = item[field] != null ? this.minutesToHHMM(item[field]) : '';
    }
  }

  /**
   * Helper to format decimal inputs on blur
   */
  onDecimalInputBlur(event: any, item: any, field: string, precision: number = 2): void {
    const input = event.target as HTMLInputElement;
    const value = input.value.trim();

    if (!value) {
      item[field] = null;
      input.value = '';
      return;
    }

    const parsed = this.parseDecimalValue(value);
    if (parsed !== null) {
      item[field] = parsed;
      input.value = this.formatDecimalPrecision(parsed, precision);
    } else {
      // Invalid input - reset to stored value or empty
      input.value = item[field] != null ? this.formatDecimalPrecision(item[field], precision) : '';
    }
  }

  // Tab management
  setTab(tab: string) {
    this.activeTab = tab;
  }

  private applyInitialTab(tab: 'project' | 'serviceCompany' | 'materialsQaqc' | 'attachments' | null): void {
    if (!tab) {
      return;
    }
    this.activeTab = tab;
  }

  requestCooperativeTests(): void {
    // Implement logic for requesting cooperative tests
    const dialogRef = this.dialog.open(CooperativeTestRequestComponent, {
      width: '900px',
      maxWidth: '95vw',
      data: {
        projectId: this.projectId,
        projectName: this.project.name || '',
        wellName: this.project.wellName || ''
      },
      disableClose: false
    });

    dialogRef.afterClosed().subscribe(() => {
    });
  }

  createSummaryReport(): void {
    // Implement logic for creating summary report
    if (!Number.isFinite(this.projectId) || this.projectId <= 0) {
      return;
    }

    this.dialog.open(SummaryReportComponent, {
      width: '95vw',           // 95% of viewport width
      maxWidth: '1200px',      // Maximum 1200px
      // height: '80vh',          // 90% of viewport height
      // maxHeight: '90vh',       // Maximum 90vh
      data: { projectId: this.projectId },  // ✅ Passes projectId without routing
      panelClass: 'summary-report-dialog'   // Custom styling class
    });
  }

  createFinalReport(): void {

    // Implement logic for creating final report

    if (!this.projectId) return;
    this.dialog.open(FinalReportDialogComponent, {
      width: 'fit-content',
      maxWidth: '96vw',
      maxHeight: '90vh',
      data: {
        projectId: this.projectId,
        projectTypeId : this.projectTypeId,
        slurries: this.project.slurries ?? [],
        serviceCompanyName: this.selectedServiceCompanyName,
        userEmail: this.userEmail ?? ''
      }
    });

  }
  openAppendClearNoteDialog(): void {
    if (!this.projectId) {
      return;
    }
    const dialogRef = this.dialog.open(AppendNoteDialogComponent, {
      width: '960px',
      disableClose: true,
      data: {
        projectId: this.projectId,
        originalNote: this.project.notes ?? ''
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result || typeof result.updatedNotes !== 'string') {
        return;
      }

      this.project.notes = result.updatedNotes;
    });
  }

  updateProject(): void {
    if (!this.projectId || this.isSaving) return;

    this.isSaving = true;
    this.saveMessage = null;

    const request: UpdateProjectRequestDto = {
      projectType: this.projectTypeId,
      ProjStatusID: this.statusTypeId,
      name: this.project.name ?? '',
      receivedBy: this.project.receivedBy ?? '',
      assignedToCAI: this.project.assignedToCAI ?? '',
      requestorCAI: this.project.requestorCAI ?? '',
      alternateRequestorCAI: this.project.alternateRequestorCAI ?? '',
      alternateCustomers: (() => {
        const val = this.project.alternateCustomers as any;
        if (Array.isArray(val)) return (val as string[]).filter((c: string) => !!c);
        if (typeof val === 'string' && (val as string).trim()) {
          return (val as string).split(',').map((s: string) => s.trim()).filter((s: string) => !!s);
        }
        return [];
      })(),
      expectedDueDate: this.project.expectedDueDate ?? null,
      rigId: this.project.rigId ?? null,
      customerProvidedWBS: this.project.customerProvidedWBS ?? '',
      sap: this.project.sap ?? '',
      isPayAsYouGo: this.project.isPayAsYouGo ?? null,
      serviceCompanyId: this.project.serviceCompanyId ?? null,
      serviceCompanyRptNumbers: this.project.serviceCompanyRptNumbers ?? '',
      notes: this.project.notes ?? '',
      mudTypeId: +(this.project.mudTypeId ?? 0),
      jobTypeId: +(this.project.jobTypeId ?? 0),
      jobSubTypeId: +(this.project.jobSubTypeId ?? 0),
      isBlendTest: !!this.project.isBlendTest,
      riserless: this.project.riserless ?? null,
      measuredDepth: this.project.measuredDepth ?? null,
      totalVerticalDepth: this.project.totalVerticalDepth ?? null,
      waterDepth: this.project.waterDepth ?? null,
      pipeSize: this.project.pipeSize ?? '',
      openHoleSize: this.project.openHoleSize ?? '',
      bhct: this.project.bhct ?? null,
      bhst: this.project.bhst ?? null,
      bhp: this.project.bhp ?? null,
      mudWeightPerGallon: this.project.mudWeightPerGallon ?? null,
      newTimeToTemperature: this.project.newTimeToTemperature ?? null,
      testSchedule: this.project.testSchedule ?? '',
      slurryServiceCompanyData: this.projectTypeId === 1 ? (this.buildSlurryScdForSave() ?? null) : null,
      spacerServiceCompanyData: this.projectTypeId === 2 ? (this.spacerServiceCompanyData ?? null) : null,
    };

    this.projectDetailsService.updateProject(this.projectId, request).subscribe({
      next: (updated: ProjectDetailsDto) => {
        this.isSaving = false;
        this.saveMessage = { text: 'Project saved successfully.', isError: false };
        // Refresh view with the server-returned data (mirrors WPF "Project = new ClientProject(businessProject)")
        this.loadProject(this.projectId);
        this.projectSaved.emit();
        setTimeout(() => { this.saveMessage = null; }, 4000);
      },
      error: (err: any) => {
        this.isSaving = false;
        this.saveMessage = {
          text: getApiErrorMessage(err, 'Project could not be saved. Please try again.'),
          isError: true
        };
      }
    });
  }

  completeProject(): void {
    // Mirrors WPF OnCompleteProject → ChangeProjectStatusAsync(projectId, Completed)
    if (!this.canCompleteProject() || this.isSaving) return;

    const confirmRef = this.dialog.open<ConfirmDialogComponent, ConfirmDialogData, boolean>(
      ConfirmDialogComponent,
      {
        width: '420px',
        disableClose: true,
        data: {
          title: 'Complete Project',
          message: 'Are you sure you want to complete this project?',
          confirmLabel: 'Complete',
          cancelLabel: 'Cancel',
        },
      }
    );

    confirmRef.afterClosed().subscribe((confirmed) => {
      if (!confirmed) return;

      this.isSaving = true;
      this.saveMessage = null;

      this.projectDetailsService.completeProject(this.projectId).subscribe({
        next: () => {
          // Mirrors WPF: Project.Status = Completed; UpdateActiveProjectStatus; MarkModelAsUnChanged
          this.project.status = 'Project Completed';
          this.originalStatus = 'Project Completed';
          this.statusTypeId = ProjectStatuses.Completed;
          this.isSaving = false;
          this.saveMessage = { text: 'Project completed successfully.', isError: false };
          this.projectSaved.emit();
          setTimeout(() => { this.saveMessage = null; }, 4000);
        },
        error: (err: any) => {
          this.isSaving = false;
          //const detail = err?.error?.detail ?? err?.message ?? 'Unknown error';
          this.dialog.open<ConfirmDialogComponent, ConfirmDialogData>(ConfirmDialogComponent, {
            width: '380px',
            data: { title: 'Error', message: `Complete project failed`, confirmLabel: 'OK', cancelLabel: '' },
          });
        }
      });
    });
  }

  // ── Slurry SCD Add/Delete helpers ──────────────────────────
  addThickeningTime(): void {
    this.slurryServiceCompanyData.thickeningTimes.push({ time26BC: null, time70BC: null, terminationTime: null, notes: null });
  }
  removeThickeningTime(i: number): void { this.slurryServiceCompanyData.thickeningTimes.splice(i, 1); }
  addFreeFluid(): void {
    this.slurryServiceCompanyData.freeFluids.push({ volume: null, percent: null, comment: null, notes: null });
  }
  removeFreeFluid(i: number): void { this.slurryServiceCompanyData.freeFluids.splice(i, 1); }
  addFluidLoss(): void {
    this.slurryServiceCompanyData.fluidLosses.push({ type: 1, calculatedVolume: null, notes: null });
  }
  removeFluidLoss(i: number): void { this.slurryServiceCompanyData.fluidLosses.splice(i, 1); }
  addRheology(): void {
    this.slurryServiceCompanyData.rheologies.push({
      type: 1, temperature: null, rpM600: null, rpM300: null, rpM200: null,
      rpM100: null, rpM60: null, rpM30: null, rpM20: null, rpM10: null,
      rpM6: null, rpM3: null, gel10Sec: null, gel10Min: null, notes: null
    });
  }
  removeRheology(i: number): void { this.slurryServiceCompanyData.rheologies.splice(i, 1); }
  addGelStrength(): void {
    this.slurryServiceCompanyData.gelStrengths.push({ type: 2, jpt: null, zeroGelTime: null, endOfPlacement: null, notes: null });
  }
  removeGelStrength(i: number): void { this.slurryServiceCompanyData.gelStrengths.splice(i, 1); }
  addFoamStability(): void {
    this.slurryServiceCompanyData.foamStabilities.push({ isStable: true, notes: null });
  }
  removeFoamStability(i: number): void { this.slurryServiceCompanyData.foamStabilities.splice(i, 1); }
  addFoamCrush(): void {
    this.slurryServiceCompanyData.foamCrushes.push({
      pressure8Hrs: null, pressure12Hrs: null, pressure24Hrs: null, pressure36Hrs: null,
      pressure48Hrs: null, pressure60Hrs: null, pressure72Hrs: null, pressure84Hrs: null,
      pressure96Hrs: null, notes: null
    });
  }
  removeFoamCrush(i: number): void { this.slurryServiceCompanyData.foamCrushes.splice(i, 1); }
  addCompStrengthUca(): void {
    this.slurryServiceCompanyData.compStrengthUcas.push({
      psiIn100: null, psiIn500: null, pressure12Hrs: null, pressure24Hrs: null,
      pressure48Hrs: null, endPSI: null, compTestEndTime: null, notes: null
    });
  }
  removeCompStrengthUca(i: number): void { this.slurryServiceCompanyData.compStrengthUcas.splice(i, 1); }
  addCompStrengthConditioned(): void {
    this.slurryServiceCompanyData.compStrengthConditionedUCAs.push({
      psiIn100: null, psiIn500: null, pressure12Hrs: null, pressure24Hrs: null,
      pressure48Hrs: null, endPSI: null, compTestEndTime: null, notes: null
    });
  }
  removeCompStrengthConditioned(i: number): void { this.slurryServiceCompanyData.compStrengthConditionedUCAs.splice(i, 1); }

  // ── Spacer SCD Add/Delete helpers ──────────────────────────
  addSpacerSSST(): void {
    if (!this.spacerServiceCompanyData) return;
    this.spacerServiceCompanyData.spacerSSSTs.push({
      conductivityOfPureSpacer: null,
      startingMudVolume: null,
      readings: [],
      notes: null
    });
  }
  removeSpacerSSST(i: number): void {
    if (!this.spacerServiceCompanyData) return;
    this.spacerServiceCompanyData.spacerSSSTs.splice(i, 1);
  }

  addSpacerSSSTReading(ssstIndex: number): void {
    if (!this.spacerServiceCompanyData) return;
    const ssst = this.spacerServiceCompanyData.spacerSSSTs[ssstIndex];
    if (!ssst) return;
    if (!ssst.readings) ssst.readings = [];
    ssst.readings.push({
      spacerAddedml: null,
      spacerPercentage: null,
      conductivity: null
    });
  }

  removeSpacerSSSTReading(ssstIndex: number, readingIndex: number): void {
    if (!this.spacerServiceCompanyData) return;
    const ssst = this.spacerServiceCompanyData.spacerSSSTs[ssstIndex];
    if (ssst?.readings) ssst.readings.splice(readingIndex, 1);
  }

  isValidNumeric(value: any): boolean {
    if (value === null || value === undefined || String(value).trim() === '') return true;
    return /^-?\d+(\.\d*)?$/.test(String(value).trim());
  }

  isValidTimeString(value: any): boolean {
    if (value === null || value === undefined || String(value).trim() === '') return true;
    return this.timeStringToMinutes(value) !== null;
  }

  get slurryScdHasErrors(): boolean {
    if (!this.slurryServiceCompanyData) return false;
    const inv = (v: any) => !this.isValidNumeric(v);
    for (const x of (this.slurryServiceCompanyData.thickeningTimes || []))
      if (false) return true; // time fields not validated
    for (const x of (this.slurryServiceCompanyData.freeFluids || []))
      if (inv(x.volume) || inv(x.percent)) return true;
    for (const x of (this.slurryServiceCompanyData.fluidLosses || []))
      if (inv(x.calculatedVolume)) return true;
    for (const x of (this.slurryServiceCompanyData.rheologies || [])) {
      if (inv(x.temperature)) return true;
      if ([x.rpM600, x.rpM300, x.rpM200, x.rpM100, x.rpM60, x.rpM30,
      x.rpM20, x.rpM10, x.rpM6, x.rpM3, x.gel10Sec, x.gel10Min].some(inv)) return true;
    }
    for (const x of (this.slurryServiceCompanyData.gelStrengths || []))
      if (inv(x.jpt) || inv(x.zeroGelTime) || inv(x.endOfPlacement)) return true;
    for (const x of (this.slurryServiceCompanyData.foamCrushes || []))
      if ([x.pressure8Hrs, x.pressure12Hrs, x.pressure24Hrs, x.pressure36Hrs,
      x.pressure48Hrs, x.pressure60Hrs, x.pressure72Hrs, x.pressure84Hrs,
      x.pressure96Hrs].some(inv)) return true;
    for (const x of ([...(this.slurryServiceCompanyData.compStrengthUcas || []),
    ...(this.slurryServiceCompanyData.compStrengthConditionedUCAs || [])]))
      if (inv(x.pressure12Hrs) ||
        inv(x.pressure24Hrs) || inv(x.pressure48Hrs) || inv(x.endPSI) ||
        inv(x.compTestEndTime)) return true;
    return false;
  }

  get spacerScdHasErrors(): boolean {
    if (!this.spacerServiceCompanyData) return false;
    const invalid = (v: any) => !this.isValidNumeric(v);
    for (const ssst of (this.spacerServiceCompanyData.spacerSSSTs || [])) {
      if (invalid(ssst.conductivityOfPureSpacer) || invalid(ssst.startingMudVolume)) return true;
      for (const r of (ssst.readings || [])) {
        if (invalid(r.spacerAddedml) || invalid(r.conductivity)) return true;
      }
    }
    for (const entry of (this.spacerServiceCompanyData.spacerQAQCs || [])) {
      for (const r of (entry.readings || [])) {
        if ([r.measuredRPM600, r.measuredRPM300, r.measuredRPM200, r.measuredRPM100,
        r.measuredRPM60, r.measuredRPM30, r.measuredRPM20, r.measuredRPM10,
        r.measuredRPM6, r.measuredRPM3].some(invalid)) return true;
      }
    }
    for (const compat of (this.spacerServiceCompanyData.spacerCompatibilities || [])) {
      for (const r of (compat.readings || [])) {
        if ([r.measuredRpm600, r.measuredRpm300, r.measuredRpm200, r.measuredRpm100,
        r.measuredRpm60, r.measuredRpm30, r.measuredRpm20, r.measuredRpm10,
        r.measuredRpm6, r.measuredRpm3].some(invalid)) return true;
      }
    }
    return false;
  }

  calculateQAQCReadingValues(reading: any): void {
    const rpm300 = parseFloat(reading.measuredRPM300);
    const rpm100 = parseFloat(reading.measuredRPM100);
    if (!isNaN(rpm300) && !isNaN(rpm100)) {
      reading.pv = (rpm300 - rpm100) * 1.5;
      reading.ty = rpm300 - reading.pv;
    } else {
      reading.pv = null;
      reading.ty = null;
    }
  }

  calculateSpacerReadingValues(reading: any, ssst: any): void {
    const spacerMl = parseFloat(reading.spacerAddedml);
    const mudVol = parseFloat(ssst.startingMudVolume);
    if (!isNaN(spacerMl) && !isNaN(mudVol) && (spacerMl + mudVol) > 0) {
      reading.spacerPercentage = parseFloat(((spacerMl / (spacerMl + mudVol)) * 100).toFixed(4));
    } else {
      reading.spacerPercentage = null;
    }

    const conductivity = parseFloat(reading.conductivity);
    const pureSpacer = parseFloat(ssst.conductivityOfPureSpacer);
    if (!isNaN(conductivity) && !isNaN(pureSpacer) && pureSpacer > 0) {
      reading.conductivityPercentage = conductivity / pureSpacer * 100;
    } else {
      reading.conductivityPercentage = null;
    }
  }

  // Create a new QAQC entry with a readings[] array (one per title)
  createEmptyQAQCEntry(): any {
    const readings = this.spacerQAQCTitles.map(title => ({
      titleId: title.id,
      title: title.fullTitle,
      measuredRPM600: null, measuredRPM300: null, measuredRPM200: null, measuredRPM100: null,
      measuredRPM60: null, measuredRPM30: null, measuredRPM20: null, measuredRPM10: null,
      measuredRPM6: null, measuredRPM3: null, pv: null, ty: null,
    }));
    return { notes: null, readings };
  }

  // Get QAQC data for a title within a specific entry (kept for compatibility)
  getQAQCDataByTitleInEntry(entry: any, titleId: number): any {
    return (entry.readings || []).find((r: any) => r.titleId === titleId) || {
      measuredRPM600: null, measuredRPM300: null, measuredRPM200: null, measuredRPM100: null,
      measuredRPM60: null, measuredRPM30: null, measuredRPM20: null, measuredRPM10: null,
      measuredRPM6: null, measuredRPM3: null, pv: null, ty: null,
    };
  }

  addSpacerQAQC(): void {
    // Add button creates a new QAQC entry with all titles
    if (!this.spacerServiceCompanyData) return;
    if (!this.spacerServiceCompanyData.spacerQAQCs) {
      this.spacerServiceCompanyData.spacerQAQCs = [];
    }
    this.spacerServiceCompanyData.spacerQAQCs.push(this.createEmptyQAQCEntry());
  }

  removeSpacerQAQC(index: number): void {
    // Remove button deletes a specific QAQC entry
    if (!this.spacerServiceCompanyData) return;
    this.spacerServiceCompanyData.spacerQAQCs.splice(index, 1);
  }

  private createEmptyCompatibilityReading(type1: number, type2: number, spacerPercent: number): any {
    return {
      id: 0,
      spacerCompatibilityRatioType1: type1,
      spacerCompatibilityRatioType2: type2,
      spacerPercent,
      measuredRpm600: null, measuredRpm300: null, measuredRpm200: null, measuredRpm100: null,
      measuredRpm60: null, measuredRpm30: null, measuredRpm20: null, measuredRpm10: null,
      measuredRpm6: null, measuredRpm3: null
    };
  }

  private buildDefaultCompatibilityReadings(): any[] {
    //Mud/Spacer ascending → 100% Cement → interleaved Cement/Spacer + Cement/Mud ascending
    return [
      this.createEmptyCompatibilityReading(1, 3, 0),  // 100% Mud
      this.createEmptyCompatibilityReading(1, 3, 5),  // 95% Mud 5% Spacer
      this.createEmptyCompatibilityReading(1, 3, 25),  // 75% Mud 25% Spacer
      this.createEmptyCompatibilityReading(1, 3, 50),  // 50% Mud 50% Spacer
      this.createEmptyCompatibilityReading(1, 3, 75),  // 25% Mud 75% Spacer
      this.createEmptyCompatibilityReading(1, 3, 95),  // 5% Mud 95% Spacer
      this.createEmptyCompatibilityReading(1, 3, 100),  // 100% Spacer
      this.createEmptyCompatibilityReading(2, 3, 0),  // 100% Cement
      this.createEmptyCompatibilityReading(2, 3, 5),  // 95% Cement 5% Spacer
      this.createEmptyCompatibilityReading(2, 1, 5),  // 95% Cement 5% Mud
      this.createEmptyCompatibilityReading(2, 3, 25),  // 75% Cement 25% Spacer
      this.createEmptyCompatibilityReading(2, 1, 25),  // 75% Cement 25% Mud
      this.createEmptyCompatibilityReading(2, 3, 50),  // 50% Cement 50% Spacer
      this.createEmptyCompatibilityReading(2, 1, 50),  // 50% Cement 50% Mud
      this.createEmptyCompatibilityReading(2, 3, 75),  // 25% Cement 75% Spacer
      this.createEmptyCompatibilityReading(2, 1, 75),  // 25% Cement 75% Mud
      this.createEmptyCompatibilityReading(2, 3, 95),  // 5% Cement 95% Spacer
      this.createEmptyCompatibilityReading(2, 1, 95),  // 5% Cement 95% Mud
    ];
  }

  addSpacerCompatibility(): void {
    if (!this.spacerServiceCompanyData) return;
    this.spacerServiceCompanyData.spacerCompatibilities.push({ readings: this.buildDefaultCompatibilityReadings(), notes: null });
  }

  removeSpacerCompatibility(i: number): void {
    if (!this.spacerServiceCompanyData) return;
    this.spacerServiceCompanyData.spacerCompatibilities.splice(i, 1);
  }

  // ── Manage QAQC Readings dialog ─────────────────────────────
  manageQAQCReadings(): void {
    const data: ManageQaqcReadingsDialogData = {
      projectId: this.projectId,
      projectStatus: this.project.status as string
    };
    const dialogRef = this.dialog.open(ManageQaqcReadingsComponent, {
      data,
      width: '620px',
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe((updatedTitles: SpacerQaqcTitleDto[] | null | undefined) => {
      if (!updatedTitles) {
        return;
      }

      this.spacerQAQCTitles = updatedTitles;
      if (this.spacerServiceCompanyData?.spacerQAQCs?.length) {
        this.spacerServiceCompanyData.spacerQAQCs = this.spacerServiceCompanyData.spacerQAQCs.map((entry: any) => {
          const existingReadings: any[] = entry.readings || [];
          const readings = this.spacerQAQCTitles.map(title => {
            const existing = existingReadings.find((r: any) => r.titleId === title.id);
            return existing ?? {
              titleId: title.id,
              title: title.fullTitle,
              measuredRPM600: null, measuredRPM300: null, measuredRPM200: null, measuredRPM100: null,
              measuredRPM60: null, measuredRPM30: null, measuredRPM20: null, measuredRPM10: null,
              measuredRPM6: null, measuredRPM3: null, pv: null, ty: null,
            };
          });
          return { ...entry, readings };
        });
      }
    });
  }
      onSpacerRowClick(s: any): void {
    const spacerId = s?.slurryID ?? s?.slurryId ?? s?.id;
    if (!spacerId) {
      console.error('Spacer row click: missing id', s);
      return;
    }
    this.openSpacerDetails(s); // pass full object
  }


  openSpacerDetails(slurry: any): void {
    const spacerId: number = slurry?.slurryID ?? slurry?.slurryId;
    if (!spacerId || spacerId <= 0) {
      console.warn('Invalid spacerId:', spacerId);
      return;
    }
    if (this.loadedProjectType === 'Spacer') {
      this.openSpacerRequested.emit({
        spacerId,
        projectId: this.projectId,
        projectName: this.project.name ?? '',
        blendTest: !!this.project.isBlendTest,
        slurry,
        spacerQAQCTitles: this.spacerQAQCTitles ?? []
      });
    }
  }

  duplicateSlurryOrSpacer(slurry: any): void {
    const slurryId: number = slurry?.slurryID ?? slurry?.slurryId;

    const dialogRef = this.dialog.open(DuplicateSpacerDialogComponent, {
      width: '700px',
      disableClose: true,
      data: { slurryId, projectId: this.projectId, type: this.loadedProjectType as 'Slurry' | 'Spacer' },
    });

    dialogRef.afterClosed().subscribe((newSlurryId: number | undefined) => {
      if (!newSlurryId) return;

      if (this.loadedProjectType === 'Slurry') {
        this.openSlurryRequested.emit({
          slurryId: newSlurryId,
          projectId: this.projectId,
          projectType: this.projectTypeId,
          projectName: this.project.name ?? '',
        });
      } else {
        this.router.navigate(['/projects-dashboard/spacer', this.projectId, newSlurryId], {
          state: {
            projectId: this.projectId,
            projectName: this.project.name ?? '',
          }
        });
      }
    });
  }
}
